/**
 * F3 — Git service context.
 *
 * VITE_GIT_SERVICE controls which adapter is used:
 *   mock (default) — MockGitService; full git pages with no server needed
 *   http            — AzureDevOpsGitService; calls /api/git/* → GitHub
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { azureDevOpsGitService } from './AzureDevOpsGitService'
import { MockGitService }        from './MockGitService'
import type { IGitService }      from './IGitService.interface'

// ── Service resolution ────────────────────────────────────────────────────────

function resolveService(): IGitService {
  const key = (import.meta.env.VITE_GIT_SERVICE as string | undefined) ?? 'mock'
  switch (key) {
    case 'http': return azureDevOpsGitService
    case 'mock':
    default:     return new MockGitService()
  }
}

const defaultService = resolveService()

// ── Context ───────────────────────────────────────────────────────────────────

const GitServiceContext = createContext<IGitService | null>(null)

interface GitServiceProviderProps {
  children: ReactNode
  /** Override the service instance — used in tests and Storybook */
  service?: IGitService
}

export function GitServiceProvider({ children, service }: GitServiceProviderProps): JSX.Element {
  const resolved = useMemo(() => service ?? defaultService, [service])

  return (
    <GitServiceContext.Provider value={resolved}>
      {children}
    </GitServiceContext.Provider>
  )
}

export function useGitService(): IGitService {
  const ctx = useContext(GitServiceContext)
  if (!ctx) throw new Error('useGitService must be used within a GitServiceProvider')
  return ctx
}
